import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/payroll/payslips")({
  component: PayslipsPage,
});

import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileDown, Loader2, Sparkles } from "lucide-react";
import PageHeader from "@/components/reusable-components/header/page-header";
import type { DateRange } from "react-day-picker";
import { DateSelector } from "@/components/crud/filters/DateSelector";
import { dayjs } from "@/lib/date-utils";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { PayslipTable } from "@/components/crud/table/PayslipTable";
import { PayslipFormSheet } from "@/components/crud/forms/PayslipFormSheet";
import { GeneratePayslipForm } from "@/components/crud/forms/GeneratePayslipForm";
import { ExportPayslipModal } from "@/components/crud/modals/ExportPayslipModal";

import useFetchPayslip, {
  type PayslipOrdering,
} from "@/hooks/api/salaries/payslips/useFetchPayslips";
import useDeletePayslip from "@/hooks/api/salaries/payslips/useDeletePayslip";
import useGeneratePayslip, {
  reportPayslipGeneration,
} from "@/hooks/api/salaries/payslips/useGeneratePayslip";
import useExportPayslips from "@/hooks/api/salaries/payslips/useExportPayslips";

import type { Payslip } from "@/types/user/payslip";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";
import type {
  GeneratePayslipPayload,
  GeneratePayslipParams,
} from "@/types/user/payslip";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import { useDebounce } from "@/hooks/use-debounce";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function PayslipsPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  const canView = can(capabilities, "salary.payslip.view");
  const canGenerate = can(capabilities, "salary.payslip.generate");
  const canDelete = can(capabilities, "salary.payslip.delete");
  const canAdjustLineItems = can(capabilities, "salary.payslip.adjust");

  // Ordering
  const { ordering, toggleOrdering, setOrdering, getApiOrdering } =
    useDataTableOrdering<
      {
        start_date: SortDirection;
        end_date: SortDirection;
        base_amount: SortDirection;
        net_amount: SortDirection;
        created_at: SortDirection;
      },
      PayslipOrdering
    >({
      start_date: null,
      end_date: null,
      base_amount: null,
      net_amount: null,
      created_at: null,
    });

  // Filters & pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchQuery, 500);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    undefined,
  );
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);

  const payslipsQuery = useFetchPayslip({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    start_date: dateRange?.from
      ? dayjs(dateRange.from).format("YYYY-MM-DD")
      : undefined,
    end_date: dateRange?.to
      ? dayjs(dateRange.to).format("YYYY-MM-DD")
      : undefined,
    isAdmin: true,
  }, { enabled: canView });
  const payslips = payslipsQuery.data?.results ?? [];
  const count = payslipsQuery.data?.count ?? 0;

  const deletePayslip = useDeletePayslip();
  const generatePayslip = useGeneratePayslip();
  const exportPayslips = useExportPayslips();

  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize]
  );

  const isMutating = deletePayslip.isPending;
  const isLoading = (canView && payslipsQuery.isPending) || isMutating;
  const isFetching = (canView && payslipsQuery.isFetching) || isMutating;

  // Sheet state
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [viewingPayslip, setViewingPayslip] = useState<Payslip | null>(null);
  const [sheetIsAdmin, setSheetIsAdmin] = useState(false);

  // Delete state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [payslipToDelete, setPayslipToDelete] = useState<Payslip | null>(null);

  // Generate dialog state
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  // Export dialog state
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const [exportUserSearch, setExportUserSearch] = useState("");
  const autoCompleteQuery = useAutoCompleteUser(
    { search: exportUserSearch || undefined },
    { enabled: isExportDialogOpen }
  );
  const users = useMemo(
    () => autoCompleteQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteQuery.data]
  );
  const autoCompleteLoading = autoCompleteQuery.isFetching;

  const resetFilters = () => {
    setOrdering({
      start_date: null,
      end_date: null,
      base_amount: null,
      net_amount: null,
      created_at: null,
    });
    setSearchQuery("");
    setDateRange(undefined);
    resetDebounce();
    setPage(1);
  };

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, dateRange, setPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleView = (payslip: Payslip) => {
    setViewingPayslip({ ...payslip });
    setSheetIsAdmin(false);
    setIsSheetOpen(true);
  };

  const handleAddLineItemFromMenu = (payslip: Payslip) => {
    setViewingPayslip({ ...payslip });
    setSheetIsAdmin(canAdjustLineItems);
    setIsSheetOpen(true);
  };

  const handleDelete = (payslip: Payslip) => {
    setPayslipToDelete(payslip);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!payslipToDelete) return;
    try {
      await deletePayslip.mutateAsync({ id: payslipToDelete.id });
      toast.success("Payslip deleted successfully");
      setIsDeleteDialogOpen(false);
      setPayslipToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleGenerate = async (
    payload: GeneratePayslipPayload,
    params: GeneratePayslipParams
  ) => {
    const result = await generatePayslip.mutateAsync({ payload, params });
    reportPayslipGeneration(result);
    setIsGenerateDialogOpen(false);
  };

  const handleExport = async (params: {
    start_date?: string;
    end_date?: string;
    user?: number;
    filename?: string;
  }) => {
    await exportPayslips.mutateAsync(params);
    toast.success("Payslips exported successfully");
    setIsExportDialogOpen(false);
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Payslips"
          description="Manage and view employee payslips."
          isLoading={isLoading}
          onToggleFilter={canView ? () => setShowFilters((prev) => !prev) : undefined}
        >
          {canView && (
            <Button
              variant="outline"
              onClick={() => setIsExportDialogOpen(true)}
              disabled={isFetching || exportPayslips.isPending}
              className="flex items-center gap-2"
            >
              {exportPayslips.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4" />
              )}
              Export
            </Button>
          )}
          {canGenerate && (
            <Button
              onClick={() => setIsGenerateDialogOpen(true)}
              disabled={isFetching || generatePayslip.isPending}
              className="flex items-center gap-2"
            >
              {generatePayslip.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Generate
            </Button>
          )}
        </PageHeader>

        {canView && (
          <>
            <FiltersPanel
              show={showFilters}
              onClear={resetFilters}
              isLoading={isFetching}
            >
              <Input
                placeholder="Search payslips..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64"
                disabled={isFetching}
              />
              <DateSelector
                value={dateRange}
                onApply={(range) => setDateRange(range)}
                onClear={() => setDateRange(undefined)}
                label="Payslip date range"
              />
            </FiltersPanel>

            <PayslipTable
              payslips={payslips}
              onView={handleView}
              onDelete={handleDelete}
              onAddLineItem={handleAddLineItemFromMenu}
              isLoading={isLoading}
              ordering={ordering}
              onOrderingChange={toggleOrdering}
              canDelete={canDelete}
              canAdjustLineItems={canAdjustLineItems}
              onNameClick={(ps) =>
                navigate({
                  to: "/admin/payroll/payslips/$userId",
                  params: { userId: String(ps.user) },
                })
              }
            />

            <PaginationComponent
              count={count}
              page={page}
              onPageChange={handlePageChange}
              isLoading={isFetching}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        )}

        <PayslipFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          payslip={viewingPayslip}
          isAdmin={sheetIsAdmin}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Payslip"
          description={`Are you sure you want to delete the payslip for ${payslipToDelete?.user_full_name}? This action cannot be undone.`}
          itemName={payslipToDelete?.user_full_name}
          isLoading={deletePayslip.isPending}
          destructive
        />

        <ExportPayslipModal
          isOpen={isExportDialogOpen}
          onClose={() => setIsExportDialogOpen(false)}
          onExport={handleExport}
          loading={exportPayslips.isPending}
          users={users}
          onSearchUsers={setExportUserSearch}
          autoCompleteLoading={autoCompleteLoading}
        />

        <GeneratePayslipForm
          isOpen={isGenerateDialogOpen}
          onClose={() => setIsGenerateDialogOpen(false)}
          onGenerate={handleGenerate}
          isLoading={generatePayslip.isPending}
        />
      </div>
    </SidebarInset>
  );
}
